<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software; you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation; either
 * version 2 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with this program;
 * if not, write to the Free Software Foundation, Inc., 51 Franklin Street, Fifth Floor,
 * Boston, MA  02110-1301, USA
 */
 -->

<template>
  <div class="orangehrm-horizontal-padding orangehrm-top-padding">
    <oxd-text tag="h6" class="orangehrm-main-title">{{
      $t('general.add_language')
    }}</oxd-text>
    <oxd-divider />
    <oxd-form :loading="isLoading" @submitValid="onSave">
      <oxd-form-row>
        <oxd-grid
          v-for="(language, index) in form.languages"
          :key="index"
          :cols="4"
          class="orangehrm-full-width-grid"
        >
          <oxd-grid-item>
            <oxd-input-field
              v-model="language.languageId"
              type="select"
              :label="$t('general.language')"
              :options="allowedLanguages"
              :rules="rules.languageId"
              :clear="false"
              required
              @update:modelValue="resetCompetency(index)"
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="language.competencyId"
              type="select"
              :label="$t('pim.competency')"
              :options="allowedCompetencies(language?.languageId)"
              :rules="rules.competencyId"
              :clear="false"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item>
            <oxd-input-field
              v-model="language.fluencyId"
              type="select"
              :label="$t('pim.fluency')"
              :rules="rules.fluencyId"
              :options="fluencies"
              :clear="false"
              required
            />
          </oxd-grid-item>
          <oxd-grid-item class="flex items-center gap-3">
            <oxd-input-field
              v-model="language.comment"
              type="textarea"
              :label="$t('general.comments')"
              :rules="rules.comment"
            />
            <oxd-icon-button
              :disabled="index == 0"
              name="trash"
              class="oxd-button--secondary"
              @click="onRemove(index)"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-form-row style="margin-bottom: 10px;">
        <oxd-button
          type="button"
          display-type="text"
          :label="$t('general.add')"
          icon-name="plus"
          @click="onAdd"
        />
      </oxd-form-row>

      <oxd-form-actions>
        <required-text />
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
        <submit-button />
      </oxd-form-actions>
    </oxd-form>
    <oxd-divider />
  </div>
</template>

<script>
import {
  required,
  shouldNotExceedCharLength,
} from '@ohrm/core/util/validation/rules';

const languageModel = {
  languageId: null,
  fluencyId: null,
  competencyId: null,
  comment: '',
};

export default {
  name: 'SaveLanguage',

  props: {
    http: {
      type: Object,
      required: true,
    },
    fluencies: {
      type: Array,
      required: true,
    },
    competencies: {
      type: Array,
      required: true,
    },
    api: {
      type: String,
      required: true,
    },
  },

  emits: ['close'],

  data() {
    return {
      isLoading: false,
      languages: [],
      rules: {
        languageId: [required],
        fluencyId: [required],
        competencyId: [required],
        comment: [shouldNotExceedCharLength(100)],
      },
      form: {
        languages: [{...languageModel}],
      },
    };
  },

  computed: {
    allowedLanguages() {
      return this.languages;
    },
  },

  beforeMount() {
    this.isLoading = true;
    this.http
      .request({
        method: 'GET',
        url: this.api,
        params: {limit: 0},
      })
      .then(response => {
        const {data} = response.data;
        if (Array.isArray(data)) {
          this.languages = data.map(item => {
            return {
              id: item.id,
              label: item.name,
              allowedCompetencyIds: item.allowedCompetencyIds
                ? item.allowedCompetencyIds
                : [],
            };
          });
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  },

  methods: {
    onSave() {
      this.isLoading = true;
      this.http
        .create({
          languages: {...this.form.languages},
        })
        .then(() => {
          return this.$toast.saveSuccess();
        })
        .then(() => {
          this.onCancel();
        });
    },
    onCancel() {
      this.$emit('close', true);
    },
    onAdd() {
      this.form.languages.push({...languageModel});
    },
    onRemove(index) {
      this.form.languages.splice(index, 1);
    },
    allowedCompetencies(language) {
      const languageIndex = this.languages.findIndex(
        item => item.id === language?.id,
      );
      if (languageIndex > -1) {
        const selectedLanguage = this.languages[languageIndex];

        let allowedOptions = this.competencies.filter(item => {
          return selectedLanguage.allowedCompetencyIds.includes(item.id);
        });

        const selectedCompentencies = [];
        if (this.form.languages?.length > 0) {
          this.form.languages.forEach(language => {
            if (
              language?.languageId?.id == selectedLanguage?.id &&
              language.competencyId?.id != null
            ) {
              selectedCompentencies.push(language.competencyId?.id);
            }
          });
        }

        if (selectedCompentencies.length > 0) {
          allowedOptions = allowedOptions.filter(option => {
            return !selectedCompentencies.includes(option?.id);
          });
        }

        return allowedOptions;
      }
      return [];
    },
    resetCompetency(index) {
      this.form.languages[index].competencyId = null;
    },
  },
};
</script>
